// /oficina (v2) — la oficina de agentes contratados. Escritorio: la disposición S11 (docs/oficina-v2/
// S11-BRIEF-DISENO.md): el mundo en píxeles a pantalla completa, el equipo flotando a la izquierda y el chat de
// un agente como ventana ENCIMA (?agente=<id>). Móvil: la lista del equipo y, al abrir uno, su chat.
import { useCallback, useEffect, useRef, useState } from 'react';
// (useRef: la última petición de abrir/cerrar la ventana «Santi» ya atendida.)
import { useSearchParams } from 'react-router-dom';
import { t, useTranslation } from '../../i18n';
import { Toaster } from '../../components/ui/sonner';
import { useSocket } from '../../hooks/useSocket';
import { useAppStore } from '../../stores/app.store';
import { useSettingsStore } from '../../stores/settings.store';
import { deshacerMover, useOficinaStore } from './store';
import { quien } from './marco/estado';
import { VentanaContratar } from './contratar/VentanaContratar';
import { VentanaChat } from './chat/VentanaChat';
import { VisorDocumento } from './chat/VisorDocumento';
import { VisorImagen } from './chat/VisorImagen';
import { Escenario } from './marco/Escenario';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { DialogoSala } from './salas/DialogoSala';
import { ListaEquipoS12 } from './marco/ListaEquipoS12';
import { Aviso, ProveedorPistas } from './ui';
import { AvisoSinConexion } from './AvisoSinConexion';
import { ActivarAvisos } from './avisos/ActivarAvisos';
import { AvisosDeSanti } from './avisos/AvisosDeSanti';
import { CapaSala, FilaWidgets } from './sala/CapaSala';
import { Saltar } from './marco/Saltar';
import { SalaMovil } from './sala/SalaMovil';
import { mensajeError } from './errores';
import { useToastStore } from '../../stores/toast.store';
import type { OrdenMesas } from './api';
import { usePelotasStore } from './pelotas/store';
import { VentanaSanti } from './pelotas/VentanaSanti';
import type { HiloAbierto } from './pelotas/tipos';
import { soloUnaVentana } from './marco/ventanas';

export function OficinaPage() {
  const { salas, contratados, hidratada, error, setAbierta, hidratar, abrirContratar } = useOficinaStore();
  // El chat usa el motor de /home: sus eventos de agentes y su lista, como allí.
  useSocket({ sinAvisos: true });
  const loadAgents = useAppStore((s) => s.loadAgents);
  // Los ajustes de Pau (idioma) los cargaba solo /home: entrando directo aquí la oficina salía en inglés.
  // useTranslation suscribe la página al idioma y sincroniza el t() de módulo que usan los componentes.
  useTranslation();
  const hidratarAjustes = useSettingsStore((s) => s.hydrate);
  useEffect(() => {
    setAbierta(true);
    void hidratar();
    void hidratarAjustes();
    loadAgents().catch(() => undefined);
    return () => {
      setAbierta(false);
      // Fuera de la oficina no hay ventana «Santi» abierta (al volver, la URL dice si la hay).
      usePelotasStore.getState().reflejarAbierto(null);
    };
  }, [setAbierta, hidratar, hidratarAjustes, loadAgents]);

  // 09-10 — ordenar las mesas a mano: se ve al instante y, si el servidor no lo acepta, vuelve atrás y lo dice.
  const ordenarMesasStore = useOficinaStore((s) => s.ordenarMesas);
  // Tras mover a alguien, un aviso con «Deshacer» (Pau aprobó la maqueta el 09-10); deshacer no trae otro aviso.
  const ordenarMesas = useCallback(
    function ordenar(projectId: string, orden: OrdenMesas, conDeshacer = true) {
      const vuelta = conDeshacer ? deshacerMover(useOficinaStore.getState().contratados, projectId, orden, quien) : undefined;
      void ordenarMesasStore(projectId, orden)
        .then(() => {
          if (vuelta)
            useToastStore.getState().push({
              message: vuelta.mensaje,
              durationMs: 6_000,
              action: { label: t('oficinaV2.mesas.deshacer'), onClick: () => ordenar(projectId, vuelta.deshacer, false) },
            });
        })
        .catch((e: unknown) => useToastStore.getState().push({ message: mensajeError(e) }));
    },
    [ordenarMesasStore],
  );

  const [params, setParams] = useSearchParams();
  const [salaEnAjustes, setSalaEnAjustes] = useState<string | null>(null);
  const agenteAbierto = params.get('agente');
  const pestanaSala = params.get('sala');
  // Una sola ventana encima de la oficina (chat, Sala de Control o Santi): abrir una cierra las otras en la MISMA
  // navegación. Antes cada función borraba solo alguna y Ctrl+K desde la Sala abría el chat escondido detrás (04-11,
  // auditoría de código B3). La URL es la verdad; los enlaces de Telegram (?agente=…) siguen igual.
  const cambiarSala = useCallback((pestana: string | null) => setParams((p) => soloUnaVentana(p, 'sala', pestana)), [setParams]);
  const abrirAgente = useCallback((id: string | null) => setParams((p) => soloUnaVentana(p, 'agente', id)), [setParams]);

  // 01-11 (pelotas v7) — la ventana «Santi» (?santi=general | ?santi=<tema> | ?santi=pedir, 03-11: «Pedir algo a
  // Santi»). La URL es la verdad (como ?agente=):
  // la lista y la ventana PIDEN abrir o cerrar (pelotas/store `peticion`) y aquí se mueve la URL; el almacén solo
  // refleja lo abierto para marcar la fila. Un solo sentido: aguanta el doble montaje de React en desarrollo y el
  // botón «atrás» del navegador cierra la ventana como cualquier otra.
  const santiEnUrl = params.get('santi');
  const santiAbierto: HiloAbierto | null =
    santiEnUrl === null ? null : santiEnUrl === 'general' || santiEnUrl === 'pedir' ? santiEnUrl : Number.isFinite(Number(santiEnUrl)) ? Number(santiEnUrl) : null;
  const reflejarAbierto = usePelotasStore((s) => s.reflejarAbierto);
  const peticionSanti = usePelotasStore((s) => s.peticion);
  const abrirSanti = usePelotasStore((s) => s.abrirSanti);
  const cerrarSanti = usePelotasStore((s) => s.cerrarSanti);
  useEffect(() => {
    reflejarAbierto(santiAbierto);
  }, [santiAbierto, reflejarAbierto]);
  const ultimaPeticion = useRef(0);
  useEffect(() => {
    if (!peticionSanti || peticionSanti.n === ultimaPeticion.current) return;
    ultimaPeticion.current = peticionSanti.n;
    const hilo = peticionSanti.hilo;
    setParams(
      (p) => soloUnaVentana(p, 'santi', hilo === null ? null : String(hilo)),
      { replace: hilo === null },
    );
  }, [peticionSanti, setParams]);
  const ventanaSanti =
    santiAbierto !== null ? (
      <VentanaSanti
        hilo={santiAbierto}
        alCerrar={cerrarSanti}
        alAbrirHilo={abrirSanti}
        alAbrirAgente={(id) => {
          cerrarSanti();
          abrirAgente(id);
        }}
      />
    ) : null;

  // 04-11 — «Activa los avisos en este ordenador…» arriba de la oficina al abrirla, solo con la oficina a la vista
  // (ninguna ventana encima: la ventana «Santi» lleva el suyo, y sobre un chat taparía su cabecera).
  const avisosAparato = hidratada && !agenteAbierto && santiAbierto === null && !pestanaSala;
  // 04-11 — el aviso de Santi dentro de la oficina (abajo a la derecha; por encima de la caja si hay una ventana abierta).
  const avisosSanti = hidratada ? <AvisosDeSanti alto={!!agenteAbierto || santiAbierto !== null} /> : null;

  // Escritorio: la oficina S11 (el mundo a pantalla completa, el equipo flotando y el chat encima), la misma
  // que Pau aprobó en las maquetas (28-09). Móvil: la lista y el chat (la oficina en píxeles no cabe).
  const escritorio = useMediaQuery('(min-width: 768px)');

  if (escritorio) {
    return (
      <ProveedorPistas delayDuration={350}>
        {hidratada ? (
          <Escenario
            salas={salas}
            contratados={contratados}
            agenteAbierto={agenteAbierto}
            alAbrir={abrirAgente}
            alCerrarChat={() => abrirAgente(null)}
            alContratar={(salaId) => abrirContratar(salaId)}
            alAjustarSala={setSalaEnAjustes}
            alOrdenarMesas={ordenarMesas}
            widgets={<FilaWidgets alCambiar={cambiarSala} />}
            chat={agenteAbierto ? <VentanaChat agentId={agenteAbierto} alCambiar={abrirAgente} alCerrar={() => abrirAgente(null)} /> : null}
          />
        ) : (
          <p className="flex h-dvh items-center justify-center bg-oficina-fondo text-o-texto text-oficina-texto3">{t('oficinaV2.abriendo')}</p>
        )}
        {error ? (
          <div className="fixed inset-x-0 top-4 z-50 mx-auto w-fit">
            <Aviso tono="error">{t('oficinaV2.errorCarga')}</Aviso>
          </div>
        ) : null}
        {hidratada ? <CapaSala pestana={pestanaSala} alCambiar={cambiarSala} /> : null}
        {hidratada ? <Saltar contratados={contratados} alAbrir={abrirAgente} alSala={() => cambiarSala('pulso')} /> : null}
        {hidratada ? ventanaSanti : null}
        <VentanaContratar alAbrirAgente={abrirAgente} />
        <VisorDocumento />
        <VisorImagen />
        <DialogoSala projectId={salaEnAjustes} alCerrar={() => setSalaEnAjustes(null)} />
        <AvisoSinConexion />
        {avisosAparato ? <ActivarAvisos donde="oficina" /> : null}
        {avisosSanti}
        <Toaster position="bottom-center" />
      </ProveedorPistas>
    );
  }

  return (
    <ProveedorPistas delayDuration={350}>
      {/* 09-10 — iPhone (PWA a pantalla completa): nada debajo de la isla/notch ni en los laterales en horizontal. El
          borde de abajo lo guarda quien lo toca (el compositor, encima de la barra de inicio o del teclado). */}
      <div className="flex h-dvh flex-col bg-oficina-fondo pl-safe-left pr-safe-right pt-safe-top font-sans text-oficina-texto antialiased">
        {/* 30-09 — la misma lista «¿qué me toca?» que en escritorio (su cabecera ya dice qué te toca y lleva
            «Contratar»). 09-10 — el chat abierto, a pantalla entera, como Telegram. */}
        <AvisoSinConexion movil />
        {avisosAparato ? <ActivarAvisos donde="oficina" movil /> : null}
        <div className={agenteAbierto ? 'min-h-0 flex-1' : 'min-h-0 flex-1 p-2 pb-[calc(theme(spacing.2)+env(safe-area-inset-bottom))]'}>
          {agenteAbierto ? (
            <VentanaChat movil agentId={agenteAbierto} alCambiar={abrirAgente} alCerrar={() => abrirAgente(null)} />
          ) : (
            <div className="flex h-full min-h-0 flex-col">
              {/* 22-10 (revisión de la sala): la Sala de Control también en el móvil; abierta, a pantalla completa. */}
              <SalaMovil pestana={pestanaSala} alCambiar={cambiarSala} />
              <div className="min-h-0 flex-1">
                <ListaEquipoS12 contratados={contratados} abierto={agenteAbierto} alAbrir={abrirAgente} alContratar={() => abrirContratar()} conCuentas />
              </div>
            </div>
          )}
          {error ? (
            <div className="fixed inset-x-0 top-[calc(1rem+env(safe-area-inset-top))] z-10 mx-auto w-fit">
              <Aviso tono="error">{t('oficinaV2.errorCarga')}</Aviso>
            </div>
          ) : null}
        </div>
        {hidratada ? ventanaSanti : null}
        {hidratada ? <Saltar contratados={contratados} alAbrir={abrirAgente} alSala={() => cambiarSala('pulso')} /> : null}
        <VentanaContratar alAbrirAgente={abrirAgente} />
        <VisorDocumento />
        <VisorImagen />
        {avisosSanti}
        <Toaster position="bottom-center" />
      </div>
    </ProveedorPistas>
  );
}

